// ============================================================
// THE DAEMONIC TREATMENT (plan body-map T1; the user chose all four looks
// and their scaling, 2026-10-09). The sheet's Daemonic trait only switches
// it on - a daemon figure is one by nature - and its strength is the codex
// tier's (engine/plate/traitLooks): the plate cannot hold it.
//
//   its lines crawl and re-form   an animated displacement over the figure
//   a warp corona                 wisps rising off its outline
//   a violet echo                 a whole second figure, a little larger,
//                                 drifting a beat behind
//   its shadow                    falling toward a light that is not there
//   its armour numbers            flicker, and now and then read wrong
//   banished                      at death it collapses to a point in a flash
//                                 and the plate is left empty
//   cracked                       a Sanctified, Force or Warp hit does not
//                                 sink into its protection: a crack of light
//                                 runs across the wound instead
//
// Pure. Zero React dependency. Frame units; seconds.
// ============================================================

import type { Point } from './frame';
import { seeded } from './hash';
import { hasSpecial } from './specials';
import { loop } from './tracks';

/**
 * The crawl's frequency, up and back down: stepped about once a second, never glided - its filter re-runs over the
 * whole figure on every write.
 */
const CRAWL_STEPS = [10, 11, 12, 13, 14, 15, 16, 15, 14, 13, 12, 11].map((f) => f / 1000);

/** Its looks' numbers at a strength (1/7 MINIMA to 1 ULTIMA), the bench's. */
export function daemonLook(t: number) {
  const r = 40 + 130 * t;
  const drift = 8 - 3 * t;
  return {
    crawl: {
      scale: Math.round(6 + 22 * t),
      seed: loop('seed', [3, 8, 13, 5, 11], Number((9 - 4 * t).toFixed(1)), { mode: 'discrete' }),
      frequency: loop('baseFrequency', CRAWL_STEPS, 11, { mode: 'discrete' }),
    },
    corona: {
      opacity: Number((0.25 + 0.55 * t).toFixed(2)),
      scale: Math.round(30 + 110 * t),
      rise: Math.round(-10 - 40 * t),
      blur: Math.round(6 + 10 * t),
      seed: loop('seed', [2, 3, 4, 5, 6, 7, 8, 9], 0.9, { mode: 'discrete' }),
    },
    echo: {
      opacity: Number((0.12 + 0.33 * t).toFixed(2)),
      scale: Number((1 + 0.05 * t).toFixed(3)),
      x: loop('x', [0, r, -r * 0.7, 0], Number(drift.toFixed(1)), { offset: 1.3 }),
      y: loop('y', [0, -r / 2, r / 3, 0], Number(drift.toFixed(1)), { offset: 1.3 }),
    },
  };
}

/** The wrong value an armour number flickers to, now and then: 1-20, never its own; seeded by its place. */
export function wrongNumber(location: string, real: number): number {
  const n = 1 + Math.floor(seeded(`wrong:${location}`)(real) * 19);
  return n >= real ? n + 1 : n;
}

/** Which light cracks a hit through Daemonic protection - holy, psychic or warp - or none (hitSoak's bypass). */
export function bypassOf(specials: readonly string[]): 'holy' | 'force' | 'warp' | null {
  if (hasSpecial(specials, 'Sanctified')) return 'holy';
  if (hasSpecial(specials, 'Force')) return 'force';
  return hasSpecial(specials, 'WarpWeapon') ? 'warp' : null;
}

/** The crack of light across a wound the protection did not hold: seven jagged legs, seeded by the hit. */
export function crackPath([x, y]: Point, seed: string): string {
  const r = seeded(seed);
  const legs = Array.from({ length: 7 }, (_, n) => {
    const i = n + 1;
    const jx = (r(`x${i}`) - 0.5) * 60;
    const jy = (r(`y${i}`) - 0.5) * 60;
    return `L${Math.round(x - 160 + (320 * i) / 7 + jx)} ${Math.round(y - 110 + (220 * i) / 7 + jy)}`;
  });
  return `M${x - 160} ${y - 110} ${legs.join(' ')}`;
}
